import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineShieldCheck } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import ConnectedClientRow from './ConnectedClientRow';

// OAuth-connected AI agents ("Connected apps" — cloud/backend/docs/integrations/mcp-server.md).
// ADMIN PREVIEW: the parent renders this only for platform admins for now; the
// backend grants API is available to every user, so exposing this to everyone
// later is purely removing the gate in IntegrationsView.

const ConnectedApps = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null); // grant id

  const loadGrants = useCallback(async () => {
    try {
      setGrants(await cloudApi.getOAuthGrants());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const handleDisconnect = async (grantId) => {
    setDisconnecting(grantId);
    try {
      await cloudApi.revokeOAuthGrant(grantId);
      setGrants((prev) => prev.filter((g) => g.id !== grantId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDisconnecting(null);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-3">
      <div className="px-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          AI agents connected through sign-in (OAuth). Disconnecting revokes their access immediately.
        </p>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200/60 dark:border-purple-700/30">
          <HiOutlineShieldCheck className="w-3 h-3" />
          Admin preview
        </span>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
          {error}
        </div>
      )}

      {!error && grants.length === 0 && (
        <div className="bg-white/40 dark:bg-gray-900/25 backdrop-blur-xl rounded-xl border border-dashed border-gray-300/60 dark:border-gray-600/40 p-5 text-sm text-gray-500 dark:text-gray-400">
          No connected apps in this workspace yet. When someone connects an AI agent through
          sign-in (e.g. adding this server to Claude and approving access), it appears here.
        </div>
      )}

      {grants.length > 0 && (
        <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm divide-y divide-gray-100 dark:divide-white/[0.04]">
          {grants.map((grant) => (
            <ConnectedClientRow
              key={grant.id}
              grant={grant}
              onDisconnect={handleDisconnect}
              disconnecting={disconnecting === grant.id}
              showConnectedBy
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectedApps;
